import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { getUser } from "../../store/reducers/user.reducer";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { PAGE_ROUTES } from "../../utils/constants";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from '@mui/material/Tooltip';
import {
  Button,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  useTheme,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import { VideoResponse } from "../../types/medial";
import VideoThumbnail from "../../assets/thumbnail.png";
import logoOnly from "../../assets/IndeeFlix-logo-only.png";
import { RWebShare } from "react-web-share";
import { Block, Delete, Edit, ReportGmailerrorred, WarningAmber } from "@mui/icons-material";
import { isUserChannel } from "../../utils";
import { deleteVideo, getFeedbackStatus, reportVideo } from "../../services/APIServices/videos.api";
import UploadVideo from "../../components/UploadVideo/UploadVideo";
import { useTranslation } from "react-i18next";
import { blockChannel, blockVideo } from "../../services/APIServices/block.api";
import { toast } from "react-toastify";

interface VideoData extends VideoResponse {
  src?: string;
  title: string;
  channelName: string;
  creationTime: string;
  views: number;
}

export let sendToAngularIframe: (data: any) => void;

// Initialize the sendToAngularIframe function
sendToAngularIframe = (data) => {
  const iframe = document.getElementById("angularAppIframe") as HTMLIFrameElement;
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(data, "http://localhost:4200");
    console.log("Sent message to Angular iframe:", data);
  } else {
    console.error("Iframe not found or not ready to receive messages.");
  }
};

const Shorts: React.FC = () => {
  const userDetails = useAppSelector((state) => getUser(state));
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [isIframeLoaded, setIframeLoaded] = useState<boolean | null>(null);
  const { verifyAuth } = useAuth();
  const { videoID } = useParams();

  useEffect(() => {
    const iframe = document.getElementById("angularAppIframe") as HTMLIFrameElement;

    const handleMessageFromAngular = (event: MessageEvent) => {
      if (event.origin !== "http://localhost:4200") {
        console.warn("Blocked unauthorized message from:", event.origin);
        return;
      }

      const { action, shareUrl, videoID, signIn, channelID } = event.data;

      if (action === "share" && shareUrl) {
        console.log("Received share URL from Angular:", shareUrl);
        navigator.clipboard.writeText(shareUrl).then(() => {
          alert("Video link copied to clipboard: " + shareUrl);
        });
      }

      if (action === "sign-in") {
        if (!signIn) {
          verifyAuth();
        }
      }

      if (action === "update-url" && videoID) {
        const newUrl = `/clips?videoID=${videoID}`;
        if (location.pathname + location.search !== newUrl) {
          navigate(newUrl, { replace: true });
        }
      }

      if (action === "handle-channel-click" && channelID) {
        navigate(`${PAGE_ROUTES.CHANNEL_PAGE}/profile/${channelID}`);
      }

      // if (action === "short-in-channel" && channelID) {
      //   const newUrl = `/shorts?videoID=${videoID}&channelID=${channelID}`;
      //   navigate(newUrl, { replace: true });

      //   sendToAngularIframe({ action: "short-in-channel", channelID, videoID  });
      // }
    };

    const handleIframeLoad = () => {
      setIframeLoaded(true);

      const token = localStorage.getItem("token");
      const isLoggedIn = !!token;

      sendToAngularIframe({ action: "token-status", isLoggedIn, userDetails, videoID });

      if (videoID) {

      }
    };

    const handleIframeError = () => {
      setIframeLoaded(false);
      console.error("Failed to load Angular app!");
    };

    window.addEventListener("message", handleMessageFromAngular);

    if (iframe) {
      iframe.addEventListener("load", handleIframeLoad);
      iframe.addEventListener("error", handleIframeError);

      if (iframe.contentWindow) {
        handleIframeLoad();
      }
    }

    return () => {
      window.removeEventListener("message", handleMessageFromAngular);
      if (iframe) {
        iframe.removeEventListener("load", handleIframeLoad);
        iframe.removeEventListener("error", handleIframeError);
      }
    };
  }, [userDetails, videoID]);

  const iframeSrc = videoID
    ? `http://localhost:4200/${videoID}`
    : "http://localhost:4200/";

  return (
    <div
      style={{
        width: "100%",
        height: "85vh",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <iframe
        id="angularAppIframe"
        src={iframeSrc}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          overflow: "hidden",
        }}
        title="Angular App"
      />
      {isIframeLoaded === true && (
        <p style={{ color: "green", textAlign: "center" }}>Angular app loaded successfully!</p>
      )}
      {isIframeLoaded === false && (
        <p style={{ color: "red", textAlign: "center" }}>Failed to load Angular app!</p>
      )}
    </div>
  );
};

export default Shorts;

export function ShortVideosCard({ data, isLoading, onVideoClick }: { data: VideoData[], isLoading: boolean, onVideoClick: (videoId: string, channelID?: string) => void }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuID, setMenuID] = useState<null | string>(null);
  const user = useAppSelector((state) => getUser(state));
  const theme = useTheme();
  const { t } = useTranslation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // State for delete confirmation dialog
  const [videoToDelete, setVideoToDelete] = useState<string>(""); // State to store the video ID to be deleted
  const [editVideoDetails, setEditVideoDetails] = useState<any>(null);
  const [editing, setEditing] = useState(false);

  const [reported, setReported] = useState(false);
  const { verifyAuth, setOnLoginSuccess } = useAuth();
  const [isSpamDialogOpen, setIsSpamDialogOpen] = useState(false);
  const [spamMessage, setSpamMessage] = useState("");
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setMenuID(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatNumber = (value: number | undefined, suffix: string): string => {
    if (value === undefined) return `0 ${suffix}`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B ${suffix}`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M ${suffix}`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K ${suffix}`;
    return `${value} ${suffix}`;
  };

  const handleShortVideo = (video: any) => {
    if (!video) return;

    // Prevent click if video is still processing or not processed
    if (
      video?.videoStatus === "Processing video" ||
      video?.videoStatus === "Video is not processed"
    ) {
      console.warn("Video not playable yet:", video?.videoStatus);
      return;
    }

    // Only proceed if video is ready
    onVideoClick(video.videoID, video.channelID || null);
  };


  const handleEdit = (videoDetails: any) => {
    console.log("Edit video");
    setEditVideoDetails(videoDetails);
    setEditing(true);
    handleMenuClose();
  };

  const handleDelete = (videoId: string) => {
    // Open delete confirmation dialog
    setVideoToDelete(videoId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteVideo(videoToDelete).then((res: any) => {
      console.log(" deleteVideo : ", res);
      // setVideos(videos.filter((video) => video.videoID !== videoToDelete));
      navigate(0);
    }, (error: any) => {
      console.log("Failed to delete video:", error);
    });
    // Close delete confirmation dialog
    setDeleteDialogOpen(false);
  };

  const fetchVideoDetails = (videoID: any) => {
    if (user?.userID) {
      try {
        getFeedbackStatus(user.userID, videoID).then(
          (feedbackStatus: any) => {
            setReported(feedbackStatus?.spam);
          }
        );
      } catch (error) {
        console.error("Error fetching video details or feedback status:", error);
      }
    }
  };

  const handleSpamClick = (videoID: any) => {
    fetchVideoDetails(videoID);
    if (verifyAuth()) {
      setIsSpamDialogOpen(true);
    } else {
      setOnLoginSuccess(() => setIsSpamDialogOpen(true));
    }
  };

  const handleBlockChannel = async (channelID: string) => {
    try {
      await blockChannel(channelID);
      toast.success("Channel blocked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block channel");
    }
  };

  const handleBlockVideo = async (videoID: string) => {
    try {
      const updated = data.filter((v: any) => v.videoID !== videoID);
      data.length = 0;
      data.push(...updated);

      await blockVideo(videoID);

      toast.success("Video blocked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block video");
    }
  };

  const handleReport = (video: any) => {
    setSelectedReason("");
    setCustomReason("");
    setIsReportDialogOpen(true);
  };

  const handleReportSubmit = (video: any) => {
    try {
      if (!selectedReason) {
        toast.warning("Please select a reason before submitting.");
        return;
      }

      const reason =
        selectedReason === "Other" && customReason.trim()
          ? `Other: ${customReason.trim()}`
          : selectedReason;

      const supportEmail = "contact@indeeclips.com";
      const subject = "Video Report";
      const body = [
        "Hello IndeeClips Team,",
        "",
        "I found an issue with the following video on the platform:",
        "",
        `Video Title: ${video?.title || "N/A"}`,
        `Channel Name: ${video?.channelName || "N/A"}`,
        `Reported Reason: ${reason}`,
        "",
        "Kindly look into this matter at the earliest and take the necessary action.",
        "",
        `Thanks & Regards,`,
        `${user?.email || "IndeeClips User"}`
      ].join("\n");

      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);

      const isMobile = /Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent || ""
      );

      if (isMobile) {
        window.location.href = `mailto:${supportEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      } else {
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${supportEmail}&su=${encodedSubject}&body=${encodedBody}`;
        window.open(gmailUrl, "_blank", "noopener,noreferrer");
      }

      setIsReportDialogOpen(false);
      toast.success("Redirecting to email...");
    } catch (error) {
      console.error("Error while reporting video:", error);
      toast.error("Failed to open email. Please try again.");
    }
  };

  return (
    <Grid container spacing={2}>
      {data.map((video: any, index) => (
        <Grid item xs={6} sm={4} lg={2} key={index}>
          <Card sx={{ position: "relative", overflow: "hidden" }}>
            {!isLoading ? (
              <>
                {/* Thumbnail */}
                <CardMedia
                  component="img"
                  sx={{
                    width: "100%",
                    aspectRatio: "9/16",
                    objectFit:
                      video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                        ? "cover"
                        : "contain",
                    backgroundColor: "#D0D4CA",
                    padding:
                      video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                        ? 0
                        : "20px",
                  }}
                  alt={video?.title}
                  image={video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                    ? video.thumbnailPic
                    : logoOnly}
                  onClick={() => handleShortVideo(video)}
                />

                {/* 🟡 Overlay for processing or not processed */}
                {(video?.videoStatus === "Processing video" ||
                  video?.videoStatus === "Video is not processed") && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        backdropFilter: "blur(1px)",
                      }}
                    >
                      {video?.videoStatus === "Processing video" ? (
                        <>
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                          <Typography
                            variant="caption"
                            mt={1}
                            sx={{ color: "#fff", fontWeight: 500 }}
                          >
                            Processing video...
                          </Typography>
                        </>
                      ) : (
                        <>
                          <WarningAmber sx={{ fontSize: 28, color: "yellow" }} />
                          <Typography
                            variant="caption"
                            mt={1}
                            sx={{
                              color: "#fff",
                              textAlign: "center",
                              fontWeight: 500,
                            }}
                          >
                            Video upload incomplete<br />Please re-upload
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
              </>
            ) : (
              /* Skeleton Loader */
              <Skeleton
                width="100%"
                sx={{
                  aspectRatio: "9/16",
                  bgcolor: "rgba(255, 255, 255, 0.12)",
                  borderRadius: "10px",
                  animation: "pulse 1.5s infinite ease-in-out",
                }}
              />
            )}

            {/* Title + Views Section */}
            <Box sx={{ p: 1 }}>
              {/* Title - Max 2 Lines */}
              {isLoading ? (
                <Skeleton variant="text" width="100%" height={20} />
              ) : (
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,  // Max 2 lines
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "14px",
                    minHeight: "40px", // Ensures space even for single-line titles
                  }}
                >
                  {video?.title}
                </Typography>
              )}

              {/* Views - Fixed Height */}
              {isLoading ? (
                <Skeleton variant="text" width="60%" height={15} />
              ) : (
                <Typography
                  variant="caption"
                  color="gray"
                  sx={{ display: "block", minHeight: "18px" }} // Fixed height for views
                >
                  {formatNumber(video?.views, t("videoCard.views"))}
                </Typography>
              )}
            </Box>

            {/* Options Icon - More Right Side */}
            <Box sx={{ position: "absolute", bottom: 15, right: 5 }}>
              <Tooltip title={t("videoCard.moreOptionsTooltip")} arrow>
                <IconButton
                  aria-label="more"
                  aria-controls={`video-menu-${index}`}
                  aria-haspopup="true"
                  onClick={(e) => handleMenuOpen(e, `video-menu-${index}`)}
                  sx={{ color: theme.palette.mode === "dark" ? "white" : "black", padding: "5px" }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>

          <Menu
            id={`video-menu-${index}`}
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && menuID === `video-menu-${index}`}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              style: {
                width: "250px",
              },
            }}
          >
            <RWebShare
              data={{
                text: video?.videoID,
                url: `${typeof window !== 'undefined' ? window.location.origin : 'https://indeeclips.com'}/watch/${video?.videoID}?type=clip`,
                title: video?.title,
              }}
            >
              <MenuItem>
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                {t("videoCard.share")}
              </MenuItem>
            </RWebShare>

            {/* Condition to check if user uploaded the video */}
            {isUserChannel(user?.channelList, video?.channelID) ? (
              <>
                <MenuItem onClick={() => handleEdit(video)}>
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.edit")}
                </MenuItem>
                <MenuItem onClick={() => handleDelete(video.videoID)}>
                  <ListItemIcon>
                    <Delete fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.delete")}
                </MenuItem>
              </>
            ) : (
              <>

                <MenuItem onClick={() => handleBlockChannel(video.channelID)}>
                  <ListItemIcon>
                    <Block fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.blockChannel")}
                </MenuItem>

                <MenuItem onClick={() => handleBlockVideo(video.videoID)}>
                  <ListItemIcon>
                    <Block fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.blockVideo")}
                </MenuItem>

                <MenuItem onClick={() => handleReport(video)}>
                  <ListItemIcon>
                    <ReportGmailerrorred fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.report")}
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleSpamClick(video.videoID)}>
                  <ListItemIcon>
                    <ReportGmailerrorred fontSize="small" />
                  </ListItemIcon>
                  {t("videoCard.spam")}
                </MenuItem>
              </>
            )}

            <Dialog open={isReportDialogOpen} onClose={() => setIsReportDialogOpen(false)}>
              <DialogTitle>{t("reportDialog.title")}</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("reportDialog.description")}
                </Typography>

                <TextField
                  select
                  fullWidth
                  label={t("reportDialog.selectReasonLabel")}
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  sx={{ mb: selectedReason === "Other" ? 2 : 0 }}
                >
                  {(t("reportDialog.reasons", { returnObjects: true }) as any).map((reason: string, idx: number) => (
                    <MenuItem key={idx} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedReason === "Other" && (
                  <TextField
                    fullWidth
                    label={t("reportDialog.enterReasonLabel")}
                    multiline
                    rows={3}
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                )}
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setIsReportDialogOpen(false)}>
                  {t("reportDialog.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleReportSubmit(video)}
                  disabled={
                    !selectedReason ||
                    (selectedReason === "Other" && !customReason.trim())
                  }
                >
                  {t("reportDialog.submit")}
                </Button>
              </DialogActions>
            </Dialog>


            <Dialog open={isSpamDialogOpen} onClose={() => setIsSpamDialogOpen(false)}>
              <DialogTitle>{t(reported ? "removeSpam" : "reportSpam")}</DialogTitle>
              <DialogContent>
                {reported ? (
                  <Typography>{t("removeReportConfirmation")}</Typography>
                ) : (
                  <TextField
                    label={t("enterReason")}
                    fullWidth
                    multiline
                    rows={4}
                    value={spamMessage}
                    onChange={(e) => setSpamMessage(e.target.value)}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIsSpamDialogOpen(false)}>{t("cancel")}</Button>
                <Button
                  onClick={() => handleReport(video.videoID)}
                  color="primary"
                  disabled={!reported && !spamMessage.trim()} // Disable if no message when reporting
                >
                  {t(reported ? "remove" : "submit")}
                </Button>
              </DialogActions>
            </Dialog>
          </Menu>
        </Grid>
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deleteDialog.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("deleteDialog.cancel")}
          </Button>
          <Button onClick={confirmDelete} color="primary">
            {t("deleteDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editing}
        onClose={() => setEditing(false)}
        fullWidth
        maxWidth="md"
      >
        <UploadVideo
          isEditing
          width="100%"
          details={editVideoDetails}
          onEditSuccess={() => setEditing(false)}
          onModalClose={() => setEditing(false)}
        />
      </Dialog>
    </Grid>
  );
}